import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { z } from "zod";
import { useEffect, useMemo, useRef, useState } from "react";
import Fuse from "fuse.js";
import { Search as SearchIcon, X } from "lucide-react";
import { ECOSYSTEM, KIND_LABEL, KIND_NOUN, type EcosystemItem, type ItemKind } from "@/lib/ecosystem";
import { GATES } from "@/lib/gates";
import { CHALLENGE_TEMPLATES } from "@/lib/challenges.data";
import { absoluteUrl } from "@/lib/site";
import { supabase } from "@/integrations/supabase/client";
import { PRESS_RESOURCES } from "@/lib/press";
import { track } from "@/lib/analytics";

const searchSchema = z.object({
  q: fallback(z.string(), "").default(""),
});

export const Route = createFileRoute("/search")({
  validateSearch: zodValidator(searchSchema),
  head: () => ({
    meta: [
      { title: "Search · Buddha KA" },
      { name: "description", content: "Search across every song, teaching, activation, gate, challenge, and book in the Buddha KA ecosystem." },
      { property: "og:title", content: "Search · Buddha KA" },
      { property: "og:description", content: "Search across every song, teaching, activation, gate, challenge, and book in the Buddha KA ecosystem." },
      { property: "og:url", content: absoluteUrl("/search") },
      // Internal search result pages are crawled but not indexed; links out of
      // them are still followed so discovery is unaffected.
      { name: "robots", content: "noindex, follow" },
    ],
    // Canonicalize every ?q=... variant back to the parameterless /search page
    // so query-string result URLs don't fragment the search index.
    links: [{ rel: "canonical", href: absoluteUrl("/search") }],
  }),



  component: SearchPage,
});

type ExtendedKind = ItemKind | "gate" | "challenge" | "academy" | "song" | "beat" | "beat_collection" | "concept" | "press";

interface SearchItem {
  key: string;
  kind: ExtendedKind;
  slug: string;
  title: string;
  subtitle: string;
  tags: string[];
  body?: string;
  // navigation
  linkKind: "typed" | "gate" | "challenge" | "academy" | "song" | "beat" | "beat_collection" | "concept" | "press";
  /** Absolute/relative href for non-router destinations (press resources). */
  externalHref?: string;
  hrefRoute?: EcosystemItem["hrefRoute"];
  gateN?: number;
  academyDomain?: string;
  songRelease?: string;
}

const norm = (s: string) => s.trim().toLowerCase();

const ECOSYSTEM_ITEMS: SearchItem[] = ECOSYSTEM.map((i) => ({
  key: `${i.kind}:${i.slug}`,
  kind: i.kind,
  slug: i.slug,
  title: i.title,
  subtitle: i.subtitle,
  tags: i.tags,
  body: i.body,
  linkKind: "typed",
  hrefRoute: i.hrefRoute,
}));

const GATE_ITEMS: SearchItem[] = GATES.map((g) => ({
  key: `gate:${g.n}`,
  kind: "gate",
  slug: String(g.n),
  title: `Gate ${g.n} — ${g.title}`,
  subtitle: g.reflection,
  tags: ["gate", `gate-${g.n}`].map(norm),
  body: [g.reflection, g.expanded, g.journalPrompt].filter(Boolean).join("\n"),
  linkKind: "gate",
  gateN: g.n,
}));

const CHALLENGE_ITEMS: SearchItem[] = CHALLENGE_TEMPLATES.map((c) => ({
  key: `challenge:${c.slug}`,
  kind: "challenge",
  slug: c.slug,
  title: c.title,
  subtitle: c.subtitle,
  tags: ["challenge", ...c.related_practices].map(norm),
  body: [c.description, c.subtitle].join("\n"),
  linkKind: "challenge",
}));

const PRESS_PAGES: Array<{ slug: string; title: string; subtitle: string; href: string }> = [
  { slug: "press", title: "Press kit", subtitle: "Bios, official portrait, usage terms, and press contact", href: "/press" },
  { slug: "factsheet", title: "Verified fact sheet", subtitle: "Citation-ready facts, bios, canonical identifiers", href: "/press/factsheet" },
  { slug: "releases", title: "Press releases", subtitle: "Formal first-party announcements", href: "/press/releases" },
];

const PRESS_ITEMS: SearchItem[] = [
  ...PRESS_PAGES.map((p) => ({
    key: `press:${p.slug}`,
    kind: "press" as const,
    slug: p.slug,
    title: p.title,
    subtitle: p.subtitle,
    tags: ["press", "media", "journalist"],
    body: p.subtitle,
    linkKind: "press" as const,
    externalHref: p.href,
  })),
  ...PRESS_RESOURCES.map((r) => ({
    key: `press:${r.id}`,
    kind: "press" as const,
    slug: r.id,
    title: r.label,
    subtitle: r.description,
    tags: ["press", "machine-readable", r.type],
    body: r.description,
    linkKind: "press" as const,
    externalHref: r.path,
  })),
];

const ALL_ITEMS: SearchItem[] = [...ECOSYSTEM_ITEMS, ...GATE_ITEMS, ...CHALLENGE_ITEMS, ...PRESS_ITEMS];

const EXT_KIND_LABEL: Record<ExtendedKind, string> = {
  ...KIND_LABEL,
  gate: "Gate",
  challenge: "Challenge",
  academy: "Academy",
  song: "Song",
  beat: "Beat",
  beat_collection: "Collection",
  concept: "Concept",
  press: "Press",
};

const EXT_KIND_NOUN: Record<ExtendedKind, string> = {
  ...KIND_NOUN,
  gate: "Consciousness Gate",
  challenge: "Journey Challenge",
  academy: "Academy Lesson",
  song: "Song / Track",
  beat: "Licensable Beat",
  beat_collection: "Beat Collection",
  concept: "Codex Concept",
  press: "Press Resource",
};

const FILTERS: Array<{ id: "all" | ExtendedKind; label: string }> = [
  { id: "all", label: "All" },
  { id: "music", label: "Music" },
  { id: "song", label: "Songs" },
  { id: "wisdom", label: "Wisdom" },
  { id: "activation", label: "Activations" },
  { id: "book", label: "Books" },
  { id: "academy", label: "Academy" },
  { id: "beat", label: "Beats" },
  { id: "beat_collection", label: "Collections" },
  { id: "concept", label: "Concepts" },
  { id: "press", label: "Press" },
  { id: "gate", label: "Gates" },
  { id: "challenge", label: "Challenges" },
];

function SearchPage() {
  const { q } = Route.useSearch();
  const navigate = useNavigate({ from: "/search" });
  const [value, setValue] = useState(q);
  const [kindFilter, setKindFilter] = useState<"all" | ExtendedKind>("all");
  const [liveItems, setLiveItems] = useState<SearchItem[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { setValue(q); }, [q]);
  useEffect(() => {
    const t = setTimeout(() => {
      if (value !== q) navigate({ search: { q: value }, replace: true });
    }, 200);
    return () => clearTimeout(t);
  }, [value, q, navigate]);

  useEffect(() => { inputRef.current?.focus(); }, []);

  // Internal search-term measurement hook. Fires on the committed query only,
  // never per keystroke, and carries no personal data.
  const lastTracked = useRef("");
  useEffect(() => {
    const term = q.trim();
    if (term.length < 2 || term === lastTracked.current) return;
    lastTracked.current = term;
    track("internal_search", { term: term.slice(0, 80) });
  }, [q]);

  // Live DB augmentation — merges published books, activations, wisdom,
  // academy lessons, songs (+ releases) with the static ecosystem. Dedupes by
  // "kind:slug" so we don't duplicate static entries.
  useEffect(() => {
    let alive = true;
    (async () => {
      const [books, acts, wisdom, lessons, songs, releases, beats, beatCollections, concepts] = await Promise.all([
        supabase.from("books").select("slug,title,subtitle,long_description,tags,author,category").limit(200),
        supabase.from("activations")
          .select("slug,title,subtitle,category,intention,instruction,phrase,tags")
          .eq("status", "published").eq("moderation_status", "approved").limit(500),
        supabase.from("wisdom_posts")
          .select("slug,title,excerpt,category,pull_quote,body")
          .eq("published", true).limit(500),
        supabase.from("academy_lessons")
          .select("slug,title,subtitle,summary,tags,difficulty,domain:academy_domains!inner(slug,name)")
          .eq("is_published", true).eq("status", "published").limit(500),
        supabase.from("songs").select("slug,title,release_slug,meaning,story,lyrics").limit(500),
        supabase.from("releases").select("slug,title,statement,frequency,type,mood,lyrics_excerpts").limit(500),
        supabase.from("beats")
          .select("slug,title,subtitle,description,genre,mood,musical_key,bpm,tags,work_kind,collection_slug")
          .eq("status", "published").limit(500),
        supabase.from("beat_collections").select("slug,name,tagline,description").eq("active", true).limit(200),
        supabase.from("codex_concepts").select("slug,title,essence,definition,cluster").limit(500),
      ]);

      const norm = (s: string) => s.trim().toLowerCase();
      const items: SearchItem[] = [];

      (books.data ?? []).forEach((b: any) => items.push({
        key: `book:${b.slug}`, kind: "book", slug: b.slug, title: b.title,
        subtitle: b.subtitle ?? b.category ?? "",
        tags: [b.author, b.category, ...(b.tags ?? [])].filter(Boolean).map(norm),
        body: b.long_description ?? "", linkKind: "typed", hrefRoute: "/books/$slug",
      }));

      (acts.data ?? []).forEach((a: any) => items.push({
        key: `activation:${a.slug}`, kind: "activation", slug: a.slug, title: a.title,
        subtitle: a.subtitle ?? a.category ?? "",
        tags: [a.category, ...(a.tags ?? [])].filter(Boolean).map(norm),
        body: [a.intention, a.instruction, a.phrase].filter(Boolean).join("\n"),
        linkKind: "typed", hrefRoute: "/activations/$slug",
      }));

      (wisdom.data ?? []).forEach((w: any) => items.push({
        key: `wisdom:${w.slug}`, kind: "wisdom", slug: w.slug, title: w.title,
        subtitle: w.excerpt ?? w.category ?? "",
        tags: [w.category].filter(Boolean).map(norm),
        body: [w.excerpt, w.pull_quote, ...(Array.isArray(w.body) ? w.body : [])].filter(Boolean).join("\n"),
        linkKind: "typed", hrefRoute: "/wisdom/$slug",
      }));

      (lessons.data ?? []).forEach((l: any) => {
        const domainSlug = l.domain?.slug;
        if (!domainSlug) return;
        items.push({
          key: `academy:${domainSlug}/${l.slug}`, kind: "academy", slug: l.slug, title: l.title,
          subtitle: l.subtitle ?? l.summary ?? l.domain?.name ?? "",
          tags: [l.difficulty, l.domain?.name, ...(l.tags ?? [])].filter(Boolean).map(norm),
          body: l.summary ?? "", linkKind: "academy", academyDomain: domainSlug,
        });
      });

      (releases.data ?? []).forEach((r: any) => items.push({
        key: `music:${r.slug}`, kind: "music", slug: r.slug, title: r.title,
        subtitle: [r.type, r.frequency].filter(Boolean).join(" · "),
        tags: [r.type, r.frequency, ...(r.mood ?? [])].filter(Boolean).map(norm),
        body: [r.statement, ...(r.lyrics_excerpts ?? [])].filter(Boolean).join("\n"),
        linkKind: "typed", hrefRoute: "/music/$slug",
      }));

      (songs.data ?? []).forEach((s: any) => items.push({
        key: `song:${s.release_slug}/${s.slug}`, kind: "song", slug: s.slug, title: s.title,
        subtitle: `Track · ${s.release_slug}`,
        tags: [s.release_slug].filter(Boolean).map(norm),
        body: [s.meaning, s.story, ...(Array.isArray(s.lyrics) ? s.lyrics : [])].filter(Boolean).join("\n"),
        linkKind: "song", songRelease: s.release_slug,
      }));

      // Published beats only — RLS and the status filter keep drafts out.
      const beatsByCollection = new Map<string, number>();
      (beats.data ?? []).forEach((b: any) => {
        if (b.collection_slug) beatsByCollection.set(b.collection_slug, (beatsByCollection.get(b.collection_slug) ?? 0) + 1);
        items.push({
          key: `beat:${b.slug}`, kind: "beat", slug: b.slug, title: b.title,
          subtitle: [b.genre, b.bpm ? `${b.bpm} BPM` : null, b.musical_key].filter(Boolean).join(" · "),
          tags: [b.genre, b.mood, b.musical_key, b.work_kind, b.bpm ? String(b.bpm) : null, ...(b.tags ?? [])]
            .filter(Boolean).map(norm),
          body: [b.subtitle, b.description].filter(Boolean).join("\n"),
          linkKind: "beat",
        });
      });

      // A collection is only searchable once it holds a published beat.
      (beatCollections.data ?? []).forEach((c: any) => {
        if (!beatsByCollection.get(c.slug)) return;
        items.push({
          key: `beat_collection:${c.slug}`, kind: "beat_collection", slug: c.slug, title: c.name,
          subtitle: c.tagline ?? "Beat collection",
          tags: ["beats", "collection"],
          body: c.description ?? "",
          linkKind: "beat_collection",
        });
      });

      (concepts.data ?? []).forEach((c: any) => items.push({
        key: `concept:${c.slug}`, kind: "concept", slug: c.slug, title: c.title,
        subtitle: c.essence ?? c.definition ?? "",
        tags: ["concept", "codex", c.cluster].filter(Boolean).map(norm),
        body: [c.essence, c.definition].filter(Boolean).join("\n"),
        linkKind: "concept",
      }));

      if (alive) setLiveItems(items);
    })().catch(() => { /* silent — static index still works */ });
    return () => { alive = false; };
  }, []);

  const mergedItems = useMemo(() => {
    const seen = new Set(ALL_ITEMS.map((i) => i.key));
    const extra = liveItems.filter((i) => !seen.has(i.key));
    return [...ALL_ITEMS, ...extra];
  }, [liveItems]);

  const fuse = useMemo(
    () =>
      new Fuse(mergedItems, {
        includeScore: true,
        threshold: 0.38,
        ignoreLocation: true,
        minMatchCharLength: 2,
        keys: [
          { name: "title", weight: 0.5 },
          { name: "subtitle", weight: 0.15 },
          { name: "tags", weight: 0.15 },
          { name: "body", weight: 0.2 },
        ],
      }),
    [mergedItems],
  );

  const results: SearchItem[] = useMemo(() => {
    const query = value.trim();
    const base = !query ? mergedItems : fuse.search(query).map((r) => r.item);
    return kindFilter === "all" ? base : base.filter((i) => i.kind === kindFilter);
  }, [value, fuse, kindFilter, mergedItems]);

  return (
    <div className="mx-auto max-w-5xl px-4 py-16 sm:px-6 sm:py-20">
      <header>
        <p className="text-xs uppercase tracking-[0.3em] text-gold">Ecosystem</p>
        <h1 className="mt-3 font-serif text-4xl sm:text-5xl">Search the transmission.</h1>
        <p className="mt-3 max-w-2xl text-muted-foreground">
          Music, songs, books, activations, wisdom, Codex concepts, gates, challenges, and press resources — one index. Search a title, a phrase, a mood, or a lyric.
        </p>
      </header>

      <div className="mt-10 flex items-center gap-3 rounded-full border hairline bg-charcoal px-5 py-3 focus-within:border-gold">
        <SearchIcon size={18} className="shrink-0 text-gold" aria-hidden />
        <input
          ref={inputRef}
          type="search"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Search titles, lyrics, teachings, gates, challenges…"
          aria-label="Search the ecosystem"
          className="w-full bg-transparent text-base outline-none placeholder:text-muted-foreground/70"
        />
        {value && (
          <button
            type="button"
            onClick={() => setValue("")}
            aria-label="Clear search"
            className="rounded-full p-1 text-muted-foreground hover:text-gold"
          >
            <X size={16} />
          </button>
        )}
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            onClick={() => setKindFilter(f.id)}
            className={`rounded-full border px-3 py-1.5 text-xs transition-colors ${
              kindFilter === f.id
                ? "border-gold bg-gold text-obsidian"
                : "hairline text-muted-foreground hover:border-gold hover:text-gold"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <p className="mt-6 text-xs text-muted-foreground">
        {results.length} {results.length === 1 ? "result" : "results"}
        {value.trim() ? ` for "${value.trim()}"` : ""}
      </p>

      <ul className="mt-6 divide-y hairline border-y hairline">
        {results.map((item) => {
          const header = (
            <>
              <p className="text-[10px] uppercase tracking-[0.3em] text-gold">
                {EXT_KIND_LABEL[item.kind]} · {EXT_KIND_NOUN[item.kind]}
              </p>
              <h2 className="mt-1 font-serif text-xl group-hover:text-gold-luminous">{item.title}</h2>
              <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{item.subtitle}</p>
            </>
          );
          const linkCls = "group flex items-baseline justify-between gap-6 py-5 hover:bg-charcoal/40";
          const arrow = <span className="shrink-0 text-xs text-gold">Enter →</span>;

          return (
            <li key={item.key}>
              {item.linkKind === "gate" && item.gateN != null ? (
                <Link to="/gates/$n" params={{ n: String(item.gateN) }} className={linkCls}>
                  <div className="min-w-0">{header}</div>
                  {arrow}
                </Link>
              ) : item.linkKind === "challenge" ? (
                <Link to="/journey/challenges" className={linkCls}>
                  <div className="min-w-0">{header}</div>
                  {arrow}
                </Link>
              ) : item.linkKind === "academy" && item.academyDomain ? (
                <Link
                  to="/academy/$domain/lesson/$lesson"
                  params={{ domain: item.academyDomain, lesson: item.slug }}
                  className={linkCls}
                >
                  <div className="min-w-0">{header}</div>
                  {arrow}
                </Link>
              ) : item.linkKind === "beat" ? (
                <Link to="/beats/$slug" params={{ slug: item.slug }} className={linkCls}>
                  <div className="min-w-0">{header}</div>
                  {arrow}
                </Link>
              ) : item.linkKind === "beat_collection" ? (
                <Link to="/beats/collections/$slug" params={{ slug: item.slug }} className={linkCls}>
                  <div className="min-w-0">{header}</div>
                  {arrow}
                </Link>
              ) : item.linkKind === "concept" ? (
                <Link to="/codex/$slug" params={{ slug: item.slug }} className={linkCls}>
                  <div className="min-w-0">{header}</div>
                  {arrow}
                </Link>
              ) : item.linkKind === "press" && item.externalHref ? (
                <a href={item.externalHref} className={linkCls}>
                  <div className="min-w-0">{header}</div>
                  {arrow}
                </a>
              ) : item.linkKind === "song" && item.songRelease ? (
                <Link
                  to="/music/$slug/song/$song"
                  params={{ slug: item.songRelease, song: item.slug }}
                  className={linkCls}
                >
                  <div className="min-w-0">{header}</div>
                  {arrow}
                </Link>
              ) : (
                <Link to={item.hrefRoute!} params={{ slug: item.slug }} className={linkCls}>
                  <div className="min-w-0">{header}</div>
                  {arrow}
                </Link>
              )}
            </li>
          );
        })}
        {results.length === 0 && (
          <li className="py-14 text-center text-sm text-muted-foreground">
            Nothing matches yet. Try a different word or clear the filter.
          </li>
        )}
      </ul>
    </div>
  );
}
